import { useEffect, useState } from "react"
import { useIntl } from "react-intl"
import { InfoCard2 } from "../../../widgets/components/UI/InfoCard2"
import { Pagination, Table } from "react-bootstrap"
import { DisplayImage } from "../../../widgets/components/General/DisplayImage"
import AddParticipants from "./AddParticipants"
import { deleteParticipants, getParticipants } from "../../core/_requests"
import { toast } from "react-toastify"
import deleteIcon from '../../../../../media/stakeholders/ic-delete.svg'
import searchIcon from '../../../../../_metronic/assets/images/svg/data-room/search-icon-grey.svg'
import { RankingInfo, rankItem } from '@tanstack/match-sorter-utils'
import {
    ColumnDef,
    ColumnFiltersState,
    FilterFn,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table'
import React from "react"
import NoDataPage from "../../../widgets/components/NoDataPage"
import SkeletonRow from "../../../digital-signing/components/SkeletonRow"
import { DeleteParticipantConfirmationModal } from "../CancelConfimrationModal"
import { useAuth } from "../../../auth"
import TagManager from "react-gtm-module"
import { GTMEvent } from "../../../../hooks/gtmHelpers"
import { useThemeMode } from "../../../../../_metronic/partials"
import brandingConfig from "../../../../../config/brandingConfig"

export default function Participants({ setSidebar, meetingId, setIsOrganizer, meetingType }: any) {
    const { formatMessage } = useIntl()
    const { mode } = useThemeMode()
    const { currentUser, companyId } = useAuth()
    const [loading, setLoading] = useState(false)
    const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
        const itemRank = rankItem(row.getValue(columnId), value)
        addMeta({ itemRank })
        return itemRank.passed
    }
    const [showCancelModal, setShowCancelModal] = useState(false)
    const [cancelLoading, setCancelLoading] = useState(false)
    const [id, setId] = useState('')
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
    // const [selectedIndex, setSelectedIndex] = useState<number | undefined>()
    const [globalFilter, setGlobalFilter] = useState('')
    const [isActive, setIsActive] = useState(false)
    const [pagination, setPagination] = useState<{
        currentPage: number
        pageSize: number
        totalCount: number
        nextPage: number
        prevPage: number | null
        totalPages: number
    }>({
        currentPage: 1,
        pageSize: 10,
        totalCount: 0,
        nextPage: 0,
        prevPage: null,
        totalPages: 0
    })
    const multiFieldGlobalFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
        const emailMatch = rankItem(row.original.email, value)
        const nameMatch = rankItem(`${row.original.name}`, value)

        // Determine which match to use based on rank comparison or other logic
        let bestMatch: RankingInfo | undefined
        if (emailMatch.passed && nameMatch.passed) {
            bestMatch = emailMatch.rank < nameMatch.rank ? emailMatch : nameMatch
        } else if (emailMatch.passed) {
            bestMatch = emailMatch
        } else if (nameMatch.passed) {
            bestMatch = nameMatch
        }

        // Only call addMeta if a best match is found
        if (bestMatch) {
            addMeta({ itemRank: bestMatch })
        }

        // Return whether the search term passed in either field
        return !!bestMatch
    }
    const [participantsData, setParticipantsData] = useState<any>([])
    const columns = React.useMemo<ColumnDef<any, any>[]>(
        () => [
            {
                accessorKey: 'name',
                id: 'Participant Name',
                header: 'Participant Name',
                cell: (info) => {
                    const { name, profileImage, email } = info.row.original; // Destructured for readability
                    return (
                        <div className='d-flex align-items-center gap-2'>
                            <DisplayImage
                                imgName={profileImage.name || (email === currentUser?.email && currentUser?.profileImg) || 'dummy'}
                                alt='profile'
                                fit='cover'
                                className='rounded-circle'
                                width={32}
                                height={32}
                            />
                            <p className='mb-0 font-size-13'>{name}</p>
                        </div>
                    );
                },
                filterFn: 'includesString',
            },
            {
                accessorKey: 'email',
                id: 'Email Id',
                header: 'Email Id',
                cell: (info) => {
                    const { email } = info.row.original;
                    return <div className='px-3 mt-2'>{email}</div>;
                },
                filterFn: 'includesString',
            },
            {
                accessorKey: 'isGuest',
                id: 'Participant Profile',
                header: 'Participant Profile',
                cell: (info) => {
                    const { isGuest } = info.row.original;
                    return <div className='px-3 mt-2'>{isGuest ? 'Guest' : 'Shareholder'}</div>;
                },
                filterFn: 'includesString',
            },
            {
                accessorKey: 'type',
                id: 'Type',
                header: 'Type',
                cell: (info) => {
                    const { type, badge } = info.row.original;
                    return <div className={`badge alert-${badge} px-4 py-2 w-100px`}><span className='flex-root'>{type}</span></div>
                },
                filterFn: 'includesString',
            },
            {
                id: 'actions',
                header: 'Actions',
                cell: ({ row }) => {
                    const { meetingParticipantId, type, email } = row.original;
                    return (
                        <div className='d-flex justify-content-between ms-4 dropdown-container mt-2'>
                            {type === 'Organizer' && email === currentUser?.email ? '' : <button
                                className='btn btn-secondry p-0 m-0'
                                onClick={() => {
                                    setShowCancelModal(true)
                                    setId(meetingParticipantId)
                                }}
                            >
                                <img src={deleteIcon} alt='Delete' />
                            </button>}
                        </div>
                    );
                },
            },
        ],
        [] // eslint-disable-line react-hooks/exhaustive-deps
    );
    const table = useReactTable({
        data: participantsData,
        columns,
        filterFns: {
            fuzzy: fuzzyFilter,
        },
        manualPagination: true,
        rowCount: pagination.totalCount,
        state: {
            columnFilters,
            globalFilter
        },
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        globalFilterFn: multiFieldGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    })
    const getMeetingParticipant = async () => {
        try {
            setLoading(true)
            const {
                data: { success, data },
            } = await getParticipants(meetingId, false, pagination.currentPage,
                pagination.pageSize)
            if (success) {
                setPagination((prev) => ({
                    ...prev,
                    currentPage: data.pagination?.currentPage,
                    pageSize: data.pagination?.pageSize,
                    totalCount: data.pagination?.totalRecords,
                    nextPage: data.pagination?.nextPage,
                    prevPage: data.pagination?.prevPage,
                    totalPages: data.pagination?.totalPages,
                }))
                const responseData = data.data.map((item: any) => {
                    let badge;
                    if (item.type === 'Host' || item.type === 'Co-Host')
                        badge = 'primary';
                    else if (item.type === 'Guest')
                        badge = 'warning';
                    else if (item.type === 'Observer')
                        badge = 'success';
                    else if (item.type === 'Organizer')
                        badge = 'info'
                    else if (item.type === 'Participant')
                        badge = 'danger'
                    else
                        badge = 'warning'

                    return {
                        ...item,
                        badge
                    };
                });
                setParticipantsData(responseData)
            }
        } catch (err) {
            console.log(err)
        } finally {
            setLoading(false)
        }
    }
    const removeParticipant = async () => {
        setCancelLoading(true)
        try {
            const {
                data: { success, error },
            } = await deleteParticipants(id)
            if (success) {
                getMeetingParticipant()
                TagManager.dataLayer({
                    dataLayer: {
                        event: GTMEvent.ParticipantDeletedSuccessful,
                        action: 'normal',
                        metadata: {
                            company_id: companyId,
                            user_id: currentUser?.userId,
                            email: currentUser?.email,
                            participant_id: id,
                        },
                    },
                })
                toast.success(formatMessage({ id: 'Participant removed successfully.' }))
            }
            else {
                TagManager.dataLayer({
                    dataLayer: {
                        event: GTMEvent.ParticipantDeleteFailed,
                        action: 'normal',
                        metadata: {
                            company_id: companyId,
                            user_id: currentUser?.userId,
                            email: currentUser?.email,
                            participant_id: id,
                            error: error[0]
                        },
                    },
                })
            }
        } catch (err) {
            TagManager.dataLayer({
                dataLayer: {
                    event: GTMEvent.ParticipantDeleteFailed,
                    action: 'normal',
                    metadata: {
                        company_id: companyId,
                        user_id: currentUser?.userId,
                        email: currentUser?.email,
                        participant_id: id,
                        error: err
                    },
                },
            })
        } finally {
            setCancelLoading(false)
            setShowCancelModal(false)
        }
    }

    const handleParticipant = async () => {
        const isPresent = participantsData.filter((data: any) => data.type === 'Organizer')
        if (isPresent.length >= 1 && participantsData.length >= 2) {
            setIsOrganizer(true)
            setSidebar(4)
        }
        else {
            toast.error(formatMessage({ id: 'At least two participants are needed for the meeting.' }))
            setIsOrganizer(false)
        }
    }

    useEffect(() => {
        if (meetingId)
            getMeetingParticipant()
        // eslint-disable-next-line
    }, [
        pagination.currentPage,
        pagination.pageSize,
        isActive
    ])// eslint-disable-line react-hooks/exhaustive-deps

    const handlePageChange = (pageNumber: number) => {
        setPagination((prev) => ({ ...prev, currentPage: pageNumber }))
    }
    return <>
        <div className="d-md-flex justify-content-between align-items-center">
            <p className='mb-4 mb-md-0'>{formatMessage({ id: 'Participants' })}</p>
            <div className="d-md-flex align-items-center gap-2">
                <form className='align-items-center d-flex w-100 border rounded mb-md-0 mb-2 position-relative'>
                    <span className='svg-icon svg-icon-3 position-absolute ms-2'>
                        <img src={searchIcon} alt='Search icon' />
                    </span>
                    <DebouncedInput
                        value={globalFilter ?? ''}
                        onChange={(value) => setGlobalFilter(String(value))}
                        className='bg-body form-control form-control-flush ps-10 rounded-2 font-size-md-12 font-size-13 font-weight-400 height-36'
                        style={{ width: 'auto !important' }}
                        placeholder='Search participant'
                    />
                </form>
                <div className='w-100'>
                    <button
                        id='dropdown-custom-components'
                        className='btn bg-primary custom-button-property text-white justify-content-center text-mute font-weight-400 font-size-13 height-36 d-flex align-items-center w-100'
                        onClick={() => {
                            if (meetingId)
                                setIsActive(true)
                            else
                                toast.error('Please complete the schedule section.')
                        }}
                    >
                        {formatMessage({ id: 'Add Participants' })}
                    </button>
                </div>
            </div>
        </div>

        <div className='mt-5 mb-9 w-100'>
            {meetingType === 'Virtual Meeting' ?
                <InfoCard2 title={formatMessage({ id: "How to Schedule Virtual Compliance Meetings?" })} desc={formatMessage({ id: `Schedule virtual compliance meetings with board members and stakeholders on ${brandingConfig.brandName} by adding scheduling details, managing participants, setting the agenda, and reviewing the details. This feature ensures organized and efficient compliance management.` })} slug={brandingConfig.helpLinks.complianceMeetingScheduleNewMeetingVirtual} /> :
                <InfoCard2 title={formatMessage({ id: "How to Schedule Physical Compliance Meetings?" })} desc={formatMessage({ id: `Schedule physical compliance meetings with board members and stakeholders on ${brandingConfig.brandName} by adding venue and scheduling details, inviting attendees, setting the agenda, and reviewing the details. This feature ensures organized and efficient compliance management.` })} slug={brandingConfig.helpLinks.complianceMeetingScheduleNewMeetingPhysical} />
            }
            <div className='bg-white rounded-2 p-4'>
                {!loading && (participantsData?.length === 0 || table.getRowModel().rows.length === 0) ? (
                    <div className='my-5 mx-6'>
                        <NoDataPage width={'w-0'} />
                    </div>
                ) : (
                    <div className="overflow-x-auto hide-scrollbar">
                        <Table className='w-100 '>
                            <thead className='custom-thead border-bottom'>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <tr
                                        key={headerGroup.id}
                                        className='font-size-12 font-weight-400 w-100 table-row border-dark-mode '
                                    >
                                        {headerGroup.headers.map((header) => (
                                            <th key={header.id} colSpan={header.colSpan} className=' pb-6 pt-4'>
                                                {header.isPlaceholder ? null : (
                                                    <div
                                                        className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                                                        onClick={header.column.getToggleSortingHandler()}
                                                    >
                                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                                        {{
                                                            asc: ' ▲',
                                                            desc: ' ▼',
                                                        }[header.column.getIsSorted() as string] ?? null}
                                                    </div>
                                                )}
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody>
                                {loading ? (
                                    <>
                                        <SkeletonRow count={5} />
                                    </>
                                ) : (
                                    table.getRowModel().rows.map((row) => (
                                        <tr key={row.id} className='border-bottom'>
                                            {row.getVisibleCells().map((cell) => (
                                                <td key={cell.id} className='pt-4 border-0'>
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>
                )}
                {!loading && participantsData?.length > 0 && (
                    <div className='w-100 d-md-flex justify-content-between align-items-center px-0 mt-8'>
                        <div className='d-flex gap-2 w-100 align-items-center justify-content-md-start justify-content-center mb-4'>
                            <select
                                aria-label='Default select example'
                                className={`p-4 py-3 w-80px border border-1 rounded-2 font-size-13 bg-white text-dark ${mode === 'dark' ? ' border-dark' : ''}`}
                                value={pagination.pageSize}
                                onChange={(e) => {
                                    const newSize = Number(e.target.value)
                                    setPagination((prev) => ({
                                        ...prev,
                                        pageSize: newSize,
                                        pageIndex: 0, // Reset to first page when page size changes
                                    }))
                                }}
                            >
                                {[10, 20, 50, 100].map((pageSize) => (
                                    <option key={pageSize} value={pageSize}>
                                        {pageSize}
                                    </option>
                                ))}
                            </select>

                            <span className='font-size-13 text-center text-md-start'>
                                {formatMessage({
                                    id: `Showing ${pagination.currentPage === 1
                                        ? 1
                                        : (pagination.currentPage - 1) * pagination.pageSize + 1
                                        } to ${pagination.totalCount > pagination.currentPage * pagination.pageSize ? pagination.currentPage * pagination.pageSize : (pagination.totalCount - pagination.currentPage * pagination.pageSize) + pagination.currentPage * pagination.pageSize} of ${pagination.totalCount} records`,
                                })}
                            </span>
                        </div>

                        <Pagination className='w-md-100 w-auto d-flex flex-md-end justify-content-center justify-content-md-end '>
                            <Pagination.First onClick={() => handlePageChange(1)} disabled={!pagination.prevPage}>
                                {'<<'}
                            </Pagination.First>

                            <Pagination.Prev
                                onClick={() => handlePageChange(pagination.currentPage - 1)}
                                disabled={!pagination.prevPage}
                            >
                                {'<'}
                            </Pagination.Prev>

                            {Array.from({ length: pagination.totalPages }, (_, i) => (
                                <Pagination.Item
                                    key={i}
                                    active={i === pagination.currentPage - 1}
                                    onClick={() => handlePageChange(i + 1)}
                                >
                                    {i + 1}
                                </Pagination.Item>
                            ))}

                            <Pagination.Next
                                onClick={() => handlePageChange(pagination.currentPage + 1)}
                                disabled={!pagination.nextPage}
                            >
                                {'>'}
                            </Pagination.Next>

                            <Pagination.Last
                                onClick={() => handlePageChange(pagination.totalPages)}
                                disabled={!pagination.nextPage}
                            >
                                {'>>'}
                            </Pagination.Last>
                        </Pagination>
                    </div>
                )}
            </div>
            {isActive && <AddParticipants isActive={isActive} setIsActive={setIsActive} />}
        </div>


        <div className='d-flex flex-md-row flex-column gap-3 justify-content-end mt-4'>
            <button
                className='btn bg-light custom-button-property text-muted fw-normal py-4 w-100 w-md-auto order-md-1 order-2'
                style={{ width: '120px' }}
                type="button"
                onClick={() => { setSidebar(2) }}
            >
                <span className='indicator-label font-size-13 p-0'>
                    {formatMessage({ id: 'Prev' })}
                </span>
            </button>
            <button
                className='btn bg-primary custom-button-property text-white fw-normal py-4 w-100 w-md-auto order-md-2 order-1'
                style={{ width: '120px' }}
                disabled={participantsData.length === 0}
                onClick={handleParticipant}
            >
                <span className='indicator-label font-size-13 p-0'>
                    {formatMessage({ id: 'Next' })}
                </span>
            </button>
        </div >

        <AddParticipants
            isActive={isActive}
            setIsActive={setIsActive}
            meetingId={meetingId}
            participantData={participantsData}
            meetingType={meetingType}
        />
        <DeleteParticipantConfirmationModal
            setDeleteShowModal={setShowCancelModal}
            showDeleteModal={showCancelModal}
            onDeleteSubmit={removeParticipant}
            deleteLoading={cancelLoading}
        />
    </>
}

function DebouncedInput({
    value: initialValue,
    onChange,
    debounce = 500,
    ...props
}: {
    value: string | number
    onChange: (value: string | number) => void
    debounce?: number
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
    const [value, setValue] = useState(initialValue)

    useEffect(() => {
        setValue(initialValue)
    }, [initialValue])

    useEffect(() => {
        const timeout = setTimeout(() => {
            onChange(value)
        }, debounce)

        return () => clearTimeout(timeout)
    }, [value, debounce, onChange])
    return <input {...props} value={value} onChange={(e) => setValue(e.target.value)} />
}